<template>
  <InfuseTable
    :columns="columns"
    :rows="rows"
    :active="(row: User, id?: number) => active(row, id)"
    :loading="loading"
    :search="search"
    :has-more-items="hasMoreItems"
    :filters="filters"
    :disabled-rows="disabledRows"
    :removable="removable"
    @selected="(value: User[]) => (selectedRows = value)"
    @remove:filter="(f: Filter) => emit('remove:filter', f)"
    @search="(query: string) => emit('search', query)"
    @load="emit('load')"
    @click-row="(row: User) => emit('clickRow', row)"
    @sort="(sort: ColumnSort) => emit('sort', sort)"
    @remove="(row: User) => emit('remove', row)"
    :remove-permission-ids="[userPermissions.DELETE, tenantUserPermissions.DELETE, organisationUserPermissions.DELETE]"
    :edit-permission-ids="[userPermissions.UPDATE, tenantUserPermissions.UPDATE, organisationUserPermissions.UPDATE]"
  >
    <template #actions>
      <InfuseButton
        data-testid="page-move-users-button"
        v-if="isDomainOrTenantUser"
        class="flex items-center whitespace-nowrap"
        color="primary-border"
        size="medium"
        @click="emit('move')"
        v-permission="{
          ids: [userPermissions.CREATE, tenantUserPermissions.SEARCH, organisationUserPermissions.SEARCH],
          hide: false
        }"
      >
        <font-awesome-icon :icon="['fas', 'repeat']" class="mr-2" />
        {{ t('iam.users.header.move_button') }}
      </InfuseButton>
      <InfuseButton
        data-testid="page-create-button"
        class="flex items-center whitespace-nowrap"
        size="medium"
        @click="emit('create')"
        v-permission="{
          ids: [userPermissions.CREATE, tenantUserPermissions.SEARCH, organisationUserPermissions.SEARCH],
          hide: false
        }"
      >
        <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
        {{ t('iam.users.header.add_button') }}
      </InfuseButton>
      <InfuseButton
        class="flex items-center whitespace-nowrap"
        color="primary-border"
        size="medium"
        @click="emit('createMachine')"
        v-permission="{
          ids: [
            machineClientPermissions.CREATE,
            tenantMachineClientPermissions.CREATE,
            organisationMachineClientPermissions.CREATE
          ],
          hide: true
        }"
      >
        <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
        {{ t('iam.users.header.add_machine_button') }}
      </InfuseButton>
    </template>

    <template #user="{ data, row }">
      <div class="flex flex-col">
        <span>{{ data }}</span>
        <span class="font-normal italic text-neutral-500">{{ row.email }}</span>
      </div>
    </template>

    <template #roles="{ data }">
      <div class="flex flex-row gap-2">
        <InfuseTag v-for="role in data.slice(0, 2)" :key="role.id">{{ role.label }}</InfuseTag>

        <InfuseTooltip
          :content="
            data
              .slice(2)
              .map((role: RoleLabel) => role.label)
              .join(', ')
          "
          placement="bottom"
        >
          <InfuseTag v-if="data.length > 2">+{{ data.length - 2 }}</InfuseTag>
        </InfuseTooltip>
      </div>
    </template>

    <template #level="{ row }">
      <div class="flex flex-row gap-2">
        <InfuseTag v-if="!row.tenantId && !row.organisationId" :inline="true">
          <font-awesome-icon :icon="['fal', 'globe']" class="mr-2" />
          <span>{{ getLevelTranslation(row) }}</span>
        </InfuseTag>

        <InfuseTag v-if="row.tenantId" :inline="true">
          <font-awesome-icon :icon="['fal', 'cubes']" class="mr-2" />
          <span>{{ row.tenantName }}</span>
        </InfuseTag>

        <InfuseTag v-if="row.organisationId" :inline="true">
          <font-awesome-icon :icon="['fal', 'cube']" class="mr-2" />
          <span>{{ row.organisationName }}</span>
        </InfuseTag>
      </div>
    </template>

    <template #type="{ row }">
      <UserTypeTag v-if="row.type" :type="row.type" />
    </template>

    <template #active="{ data }">
      <font-awesome-icon
        :icon="['fal', data ? 'circle-check' : 'circle-xmark']"
        class="h-5 w-5"
        :class="data ? 'text-success-500' : 'text-error-500'"
      />
    </template>

    <template #createdAt="{ data }">
      {{ formatDate(data) }}
    </template>

    <template #lastLogin="{ data }">
      {{ data ? formatDate(data) : '-' }}
    </template>
  </InfuseTable>
</template>

<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { userPermissions } from '@infuse/api/iam/user';
import { vPermission } from '@infuse/iam/permission/directives';
import { RoleLabel } from '@infuse/iam/role/type';
import { useAssetLevel } from '@infuse/iam/composables';
import { User } from '@infuse/iam/user/type';
import { InfuseButton, InfuseTable, InfuseTag, InfuseTooltip } from '@infuse/ui/components';
import {
  type ColumnSort,
  type Filter,
  type FilterConfiguration,
  type UiTableColumn,
  UiTableColumnAlignment
} from '@infuse/ui/domain';
import { formatDate } from '@infuse/ui/utils';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { tenantUserPermissions, tenantMachineClientPermissions } from '@infuse/api/iam/tenant';
import { organisationUserPermissions, organisationMachineClientPermissions } from '@infuse/api/iam/organisation';
import { machineClientPermissions } from '@infuse/api/iam/user';
import { useCurrentUserLevel } from '@infuse/iam/composables';
import UserTypeTag from './UserTypeTag.vue';

const { t } = useI18n();
const { getLevelTranslation } = useAssetLevel();
const { isDomainOrTenantUser } = useCurrentUserLevel();

interface Emits {
  (e: 'load'): void;

  (e: 'clickRow', row: User): void;

  (e: 'sort', sort: ColumnSort): void;

  (e: 'add:filter', filter: Filter): void;

  (e: 'remove:filter', filter: Filter): void;

  (e: 'remove', row: User): void;

  (e: 'create'): void;

  (e: 'createMachine'): void;

  (e: 'move'): void;

  (e: 'search', query: string): void;
}

const emit = defineEmits<Emits>();
const selectedRows = ref<User[]>([]);

interface Props {
  rows: User[];
  active?: (row: User, id?: number) => boolean;
  loading: boolean;
  page: number;
  searchQuery?: string;
  searchEnabled: boolean;
  filters?: Filter[];
  filterConfigs?: FilterConfiguration[];
  removable?: boolean;
  hasMoreItems: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  rows: () => [],
  active: () => false,
  loading: false,
  searchQuery: '',
  searchEnabled: true,
  removable: false,
  hasMoreItems: true
});

const search = computed(() =>
  !props.searchEnabled
    ? undefined
    : {
        placeholder: t('iam.users.search_placeholder'),
        query: props.searchQuery
      }
);

const disabledRows = computed(() => {
  const row = selectedRows.value[0];
  if (!row) {
    return [];
  }

  return props.rows.filter((user) => user.id !== row.id);
});

const columns: UiTableColumn[] = [
  {
    name: 'user',
    label: () => t('iam.users.user'),
    field: 'name',
    bold: true
  },
  {
    name: 'type',
    label: () => t('iam.users.type.label'),
    field: 'type'
  },
  {
    name: 'roles',
    label: () => t('iam.users.roles'),
    field: 'roleLabels'
  },
  {
    name: 'level',
    label: () => t('iam.users.level'),
    field: 'level'
  },
  {
    name: 'active',
    label: () => t('iam.users.active'),
    field: 'active',
    align: UiTableColumnAlignment.CENTER
  },
  {
    name: 'createdAt',
    label: () => t('iam.users.createdAt'),
    field: 'createdAt',
    sortable: true
  },
  {
    name: 'lastLogin',
    label: () => t('iam.users.lastLogin'),
    field: 'lastLogin',
    sortable: true
  }
];
</script>
